import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { HOME_SEO as S } from "../../data/homeSeo";
import { CATEGORIES } from "../../data/categories";

const box = "rounded-[18px] border-[3px] p-3 sm:p-4";
const h2 = "ua-title text-2xl leading-none sm:text-3xl";

function Hero() {
  const start = () => document.getElementById("home-categories")?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div className={`${box} border-[#FFC15E] bg-[#2A0F45] text-white md:col-span-2`} data-testid="seo-hero">
      <p className={h2}>The Fun Decision-Making App</p>
      <h2 className="mt-1 font-comic text-sm text-[#FFE14D] sm:text-base" data-testid="home-h2">{S.tagline}</h2>
      {S.intro.map((p) => <p key={p} className="mt-2 text-sm font-semibold text-white/90">{p}</p>)}
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={start} data-testid="seo-start-cta" className="ua-btn rounded-full border-[3px] border-[#3b1800] bg-[#FFE14D] px-5 py-2 text-base text-[#3b1800] transition-transform hover:-translate-y-0.5 active:translate-y-0.5">
          Start Dithering — It's Free
        </button>
      </div>
    </div>
  );
}

function Decides() {
  return (
    <div className={`${box} border-[#FFB36B] bg-white md:col-span-2 lg:col-span-4`} data-testid="seo-categories">
      <h2 className={`${h2} text-[#FF5D1F]`}>What Can DITHER Decide?</h2>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {S.categories.map((c) => {
          const Icon = CATEGORIES.find((x) => x.id === c.id)?.icon;
          return (
            <Link key={c.id} to={c.id === "presents" ? "/presents" : `/category/${c.id}`} data-testid={`seo-category-${c.id}`} className="group rounded-xl border-2 border-[#FFD9A8] bg-[#FFF6E6] p-2 transition-colors hover:bg-[#FFE9C7]">
              <h3 className="flex items-center gap-1.5 font-comic text-base text-[#3b1800]">{Icon && <Icon className="h-4 w-4 text-[#FF5D1F]" strokeWidth={3} />}{c.heading}</h3>
              <p className="text-xs font-bold text-[#FF5D1F]">{c.question}</p>
              <p className="mt-0.5 text-xs text-[#6b3a12]">{c.blurb}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function TextBox({ title, lines, tone, testId, links = [] }: { title: string; lines: string[]; tone: string; testId: string; links?: { to: string; label: string }[] }) {
  return (
    <div className={`${box} ${tone}`} data-testid={testId}>
      <h2 className={h2}>{title}</h2>
      {lines.map((l) => <p key={l} className="mt-1.5 text-sm font-semibold">{l}</p>)}
      {!!links.length && <div className="mt-2 flex flex-wrap gap-1.5">{links.map((l) => (
        <Link key={l.to} to={l.to} data-testid={`seo-link${l.to.replace(/\//g, "-")}`} className="rounded-full border-2 border-[#3b1800] bg-white px-3 py-1 font-comic text-xs text-[#3b1800] transition-transform hover:-translate-y-0.5">{l.label} →</Link>
      ))}</div>}
    </div>
  );
}

function Faq() {
  return (
    <div className={`${box} border-[#FFC15E] bg-white text-[#3b1800] md:col-span-2 lg:col-span-4`} data-testid="seo-faq">
      <h2 className={`${h2} text-[#FF5D1F]`}>Frequently Asked Questions</h2>
      <div className="mt-2 grid items-start gap-1.5 md:grid-cols-2">
        {S.faqs.map((f, i) => (
          <details key={f.q} className="group rounded-xl border-2 border-[#FFD9A8] bg-[#FFF6E6] px-3 py-2" data-testid={`seo-faq-item-${i}`}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 font-comic text-sm">
              <h3>{f.q}</h3><ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" strokeWidth={3} />
            </summary>
            <p className="mt-1 text-sm text-[#6b3a12]">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

export function HomeSeoSection() {
  return (
    <section className="grid gap-2 sm:gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid="home-seo-section">
      <Hero />
      <TextBox title="Stuck? Let DITHER Decide." lines={S.stuck} tone="border-[#FFC15E] bg-gradient-to-br from-[#FF7A1A] to-[#FF5D1F] text-white md:col-span-2" testId="seo-stuck" />
      <Decides />
      <TextBox title="Perfect for Couples, Friends & Families" lines={[S.people]} tone="border-[#C9A3FF] bg-[#E2C9FF] text-[#3b1800] lg:col-span-2" testId="seo-people" links={[{ to: "/couples", label: "Decision maker for couples" }, { to: "/group/new", label: "Group vote for friends" }, { to: "/category/night-out", label: "Night out with friends" }, { to: "/category/tv-film", label: "Family film night" }, { to: "/category/chores", label: "Family chore picker" }]} />
      <TextBox title="Make Everyday Decisions More Fun" lines={[...S.everyday, S.everydayOutro]} tone="border-[#9FE59F] bg-[#CFF5CF] text-[#3b1800] lg:col-span-2" testId="seo-everyday" links={[{ to: "/what-should-i-eat", label: "What should I eat?" }, { to: "/what-should-we-watch", label: "What should we watch?" }, { to: "/what-should-we-do-tonight", label: "What should we do tonight?" }, { to: "/who-does-the-dishes", label: "Who does the dishes?" }]} />
      <Faq />
    </section>
  );
}
